import { useState } from "react";
import { X, Trash2, Loader2, ShoppingBag, Minus, Plus, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const fmt = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);
const inp = "w-full border border-charcoal/20 bg-white px-3 py-2 text-sm outline-none focus:border-forest";
const BLANK = { first_name: "", last_name: "", email: "", phone: "", country: "US", region: "", address1: "", address2: "", city: "", zip: "" };

export function CartDrawer() {
  const { items, open, setOpen, subtotal, count, setQty, remove } = useCart();
  const [step, setStep] = useState("cart"); // cart | address
  const [addr, setAddr] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [totals, setTotals] = useState(null);
  const [code, setCode] = useState("");

  if (!open) return null;

  const payload = () => ({
    items: items.map((x) => ({ product_id: x.product_id, variant_id: x.variant_id, quantity: x.quantity })),
    address: addr, origin_url: window.location.origin,
    discount_code: code.trim() || undefined,
  });

  const goToAddress = () => { setStep("address"); setTotals(null); };

  const calcShipping = async () => {
    const req = ["first_name", "last_name", "email", "country", "address1", "city", "zip"];
    if (req.some((k) => !addr[k]?.trim())) { toast.error("Please fill in the required fields"); return; }
    setBusy(true);
    try { setTotals(await api.shopShipping(payload())); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't calculate shipping"); }
    finally { setBusy(false); }
  };

  const checkout = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await api.shopCheckout(payload());
      window.location.href = checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-forest/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-md bg-paper h-full overflow-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-charcoal/10 sticky top-0 bg-paper z-10">
          <div className="flex items-center gap-2">
            {step === "address" && <button onClick={() => setStep("cart")} data-testid="cart-back"><ChevronLeft size={20} className="text-charcoal" /></button>}
            <h2 className="font-serif text-2xl text-forest">{step === "cart" ? `Your cart (${count})` : "Shipping details"}</h2>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close cart" data-testid="cart-close"><X size={22} className="text-charcoal" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div><ShoppingBag size={40} className="mx-auto text-charcoal/30" /><p className="text-charcoal/60 mt-3">Your cart is empty.</p></div>
          </div>
        ) : step === "cart" ? (
          <>
            <div className="flex-1 p-5 space-y-4">
              {items.map((x) => (
                <div key={`${x.product_id}-${x.variant_id}`} className="flex gap-3" data-testid={`cart-item-${x.product_id}-${x.variant_id}`}>
                  {x.image && <img src={x.image} alt="" className="w-16 h-16 object-cover border border-charcoal/10" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-forest line-clamp-2">{x.title}</p>
                    {x.variant_title && <p className="text-xs text-charcoal/50">{x.variant_title}</p>}
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center border border-charcoal/20">
                        <button onClick={() => setQty(x.product_id, x.variant_id, x.quantity - 1)} className="px-2 py-1" aria-label="Decrease"><Minus size={13} /></button>
                        <span className="px-2 text-sm" data-testid="cart-item-qty">{x.quantity}</span>
                        <button onClick={() => setQty(x.product_id, x.variant_id, x.quantity + 1)} className="px-2 py-1" aria-label="Increase"><Plus size={13} /></button>
                      </div>
                      <span className="text-sm font-semibold text-forest">{fmt(x.price * x.quantity)}</span>
                    </div>
                  </div>
                  <button onClick={() => remove(x.product_id, x.variant_id)} className="text-charcoal/40 hover:text-red-600 self-start" aria-label="Remove" data-testid="cart-item-remove"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-charcoal/10 sticky bottom-0 bg-paper">
              <div className="flex justify-between text-sm mb-1"><span className="text-charcoal/60">Subtotal</span><span className="font-semibold text-forest">{fmt(subtotal)}</span></div>
              <p className="text-xs text-charcoal/45 mb-3">Shipping calculated at the next step.</p>
              <button onClick={goToAddress} className="w-full bg-forest text-bone py-3 font-semibold hover:bg-forest-2 transition-colors" data-testid="cart-checkout-btn">Checkout</button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="First name *" value={addr.first_name} onChange={(e) => setAddr({ ...addr, first_name: e.target.value })} data-testid="addr-first" />
                <input className={inp} placeholder="Last name *" value={addr.last_name} onChange={(e) => setAddr({ ...addr, last_name: e.target.value })} data-testid="addr-last" />
              </div>
              <input className={inp} placeholder="Email *" type="email" value={addr.email} onChange={(e) => setAddr({ ...addr, email: e.target.value })} data-testid="addr-email" />
              <input className={inp} placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="addr-phone" />
              <input className={inp} placeholder="Address line 1 *" value={addr.address1} onChange={(e) => setAddr({ ...addr, address1: e.target.value })} data-testid="addr-address1" />
              <input className={inp} placeholder="Address line 2" value={addr.address2} onChange={(e) => setAddr({ ...addr, address2: e.target.value })} data-testid="addr-address2" />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="City *" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="addr-city" />
                <input className={inp} placeholder="State / region" value={addr.region} onChange={(e) => setAddr({ ...addr, region: e.target.value })} data-testid="addr-region" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} placeholder="ZIP / postal *" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} data-testid="addr-zip" />
                <input className={inp} placeholder="Country (ISO2) *" maxLength={2} value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value.toUpperCase() })} data-testid="addr-country" />
              </div>
              {!totals ? (
                <button onClick={calcShipping} disabled={busy} className="w-full border border-forest text-forest py-2.5 font-semibold hover:bg-forest hover:text-bone transition-colors mt-2" data-testid="calc-shipping-btn">
                  {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Calculate shipping"}
                </button>
              ) : (
                <div data-testid="cart-totals-wrap">
                  <div className="flex gap-2 mt-2">
                    <input className={inp} placeholder="Promo code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="promo-code-input" />
                    <button onClick={calcShipping} disabled={busy} className="shrink-0 border border-forest text-forest px-4 font-semibold text-sm hover:bg-forest hover:text-bone transition-colors" data-testid="promo-apply-btn">Apply</button>
                  </div>
                  <div className="bg-bone border border-saddle/20 p-4 mt-2 text-sm" data-testid="cart-totals">
                    <div className="flex justify-between"><span className="text-charcoal/60">Subtotal</span><span>{fmt(totals.subtotal)}</span></div>
                    {totals.discount_cents > 0 && (
                      <div className="flex justify-between mt-1 text-forest" data-testid="totals-discount"><span>Discount ({totals.discount_code})</span><span>−{fmt(totals.discount_cents)}</span></div>
                    )}
                    <div className="flex justify-between mt-1"><span className="text-charcoal/60">Shipping</span><span>{totals.free_shipping ? "Free" : fmt(totals.shipping)}</span></div>
                    <div className="flex justify-between mt-2 pt-2 border-t border-charcoal/10 font-semibold text-forest"><span>Total</span><span>{fmt(totals.total)}</span></div>
                  </div>
                </div>
              )}
            </div>
            {totals && (
              <div className="p-5 border-t border-charcoal/10 sticky bottom-0 bg-paper">
                <button onClick={checkout} disabled={busy} className="w-full bg-blaze text-white py-3 font-semibold hover:brightness-95 transition-all" data-testid="cart-pay-btn">
                  {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : `Pay ${fmt(totals.total)}`}
                </button>
                <p className="text-[11px] text-charcoal/45 mt-2 text-center">Secure checkout by Stripe. Fulfilled by Printify.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
